import React, { useState } from "react";
import styled from "styled-components";
import { Button } from "antd";
import { HeartOutlined, StarFilled } from "@ant-design/icons";

const Experiences = styled.div`
  padding-top: 7rem;
  padding-left: 8rem;
  padding-right: 8rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const SmallHeader = styled.h2`
  text-align: left;
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  line-height: 100%;
`;

const ExperiencesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const ExperiencesRow = styled.div`
  display: contents;
`;

const ExperienceCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ExperienceCard = styled.div`
  border-radius: 16px;
  height: 250px;
  position: relative;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.7) 100%);
    border-radius: 16px;
  }
`;

const ExperienceCardTop = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 1rem;
  position: relative;
  z-index: 2;
`;

const ExperienceCardBottom = styled.div`
  padding: 0 0.5rem;
  color: #333;
`;

const LoadMoreButton = styled(Button)`
  background: #1890ff;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0 24px;
  height: 40px;
  font-weight: 500;

  &:hover {
    background: #40a9ff;
    color: white;
  }
`;

const LikeButton = styled(Button)`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(240, 240, 240, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #333;

  .anticon {
    color: #333;
    font-size: 16px;
  }
`;

const ExperienceTitle = styled.h3`
  color: #333;
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 8px 0;
`;

const ExperienceRating = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const RatingNumber = styled.span`
  color: #333;
  font-size: 14px;
  font-weight: 500;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 2rem;
`;

// Sample data for popular experiences
const popularExperiences = [
  { name: "Freedom Park", rating: 4.8 },
  { name: "Magic Land", rating: 4.5 },
  { name: "Olumo Rock", rating: 4.7 },
  { name: "Lekki Conservation Centre", rating: 4.6 },
  { name: "National Museum Lagos", rating: 4.2 },
  { name: "Nike Art Gallery", rating: 4.4 },
  { name: "Tarkwa Bay Beach", rating: 4.3 },
  { name: "Kalakuta Republic Museum", rating: 4.1 },
  { name: "Badagry Slave Museum", rating: 4.0 },
  { name: "Elegushi Beach", rating: 4.2 },
  { name: "New Afrika Shrine", rating: 4.3 },
  { name: "Coconut Beach", rating: 4.1 },
];

const experienceImages = [
  "https://images.unsplash.com/photo-1583258292688-d0213dc5a3a8",
  "https://images.unsplash.com/photo-1506929562872-bb421503ef21",
  "https://images.unsplash.com/photo-1503917988258-f87a78e3c995",
  "https://images.unsplash.com/photo-1506197603052-3cc9c3a201bd",
  "https://images.unsplash.com/photo-1506260408121-e353d10b87c7",
  "https://images.unsplash.com/photo-1504280390367-361c6d9f38f4",
  "https://images.unsplash.com/photo-1563841930606-67e2bce48b78",
  "https://images.unsplash.com/photo-1509316785289-025f5b846b35",
  "https://images.unsplash.com/photo-1544551763-46a013bb70d5",
  "https://images.unsplash.com/photo-1520250497591-112f2f40a3f4",
  "https://images.unsplash.com/photo-1506377247377-2a5b3b417ebb",
  "https://images.unsplash.com/photo-1518621736915-f3b1c41bfd00",
];

const ExperiencesSection: React.FC = () => {
  const [visibleRows, setVisibleRows] = useState(2); // Show 2 rows initially (8 cards)
  const cardsPerRow = 4;

  const loadMore = () => {
    setVisibleRows(prev => prev + 1); // Add one more row (4 more cards)
  };

  // Calculate total cards to display
  const totalCardsToShow = visibleRows * cardsPerRow;
  const hasMoreCards = totalCardsToShow < popularExperiences.length;

  return (
    <Experiences>
      <SmallHeader>Experiences you&apos;d love to see</SmallHeader>
      <ExperiencesContainer>
        {Array.from({ length: visibleRows }).map((_, rowIndex) => (
          <ExperiencesRow key={rowIndex}>
            {popularExperiences.slice(rowIndex * cardsPerRow, (rowIndex + 1) * cardsPerRow).map((experience, index) => {
              const cardIndex = rowIndex * cardsPerRow + index;
              return (
                <ExperienceCardWrapper key={cardIndex}>
                  <ExperienceCard
                    style={{ backgroundImage: `url(${experienceImages[cardIndex % experienceImages.length]})` }}
                  >
                    <ExperienceCardTop>
                      <LikeButton icon={<HeartOutlined />} />
                    </ExperienceCardTop>
                  </ExperienceCard>
                  <ExperienceCardBottom>
                    <ExperienceTitle>{experience.name}</ExperienceTitle>
                    <ExperienceRating>
                      <StarFilled style={{ color: "#FFD700" }} />
                      <RatingNumber>{experience.rating}</RatingNumber>
                    </ExperienceRating>
                  </ExperienceCardBottom>
                </ExperienceCardWrapper>
              );
            })}
          </ExperiencesRow>
        ))}
      </ExperiencesContainer>
      {hasMoreCards && (
        <ButtonContainer>
          <LoadMoreButton onClick={loadMore}>Display More</LoadMoreButton>
        </ButtonContainer>
      )}
    </Experiences>
  );
};

export default ExperiencesSection;
